import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserCircle, Plus, Trash2, Mail, Edit, Users as UsersIcon, AlertCircle, CheckCircle2, Phone, Eye, Building, MapPin, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import CaptchaWidget from "@/components/CaptchaWidget";
import { validateEmail, validateIndianPhone } from "@/utils/validation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Users() {
  const [users, setUsers] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    role: "agent",
    team_leader_id: "",
    contact_number: "",
    // Developer details
    developer_name: "",
    developer_details: "",
    developer_address: "",
    developer_contact: "",
    developer_google_location: "",
    // Mandate company details
    mandate_company_name: "",
    mandate_company_details: "",
    mandate_company_address: "",
    mandate_company_contact: "",
    sourcing_type: "",
  });
  
  // Validation states
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const recaptchaRef = useRef(null);

  useEffect(() => {
    fetchUsers();
    fetchTeamLeaders();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Filter out property seekers (role: "user") - they have their own management page
      const teamMembers = response.data.filter(u => u.role !== "user");
      setUsers(teamMembers);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamLeaders = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users/team-leaders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTeamLeaders(response.data);
    } catch (error) {
      // Silently fail - team leaders list is optional
      console.log("Could not fetch team leaders");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Real-time validation
    if (name === 'email' && value) {
      const result = validateEmail(value);
      setEmailError(result.error || "");
    } else if (name === 'email') {
      setEmailError("");
    }
    
    if (name === 'contact_number' && value) {
      const result = validateIndianPhone(value);
      setPhoneError(result.error || "");
    } else if (name === 'contact_number') {
      setPhoneError("");
    }
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token || "");
    setCaptchaError("");
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken("");
    setCaptchaError("CAPTCHA expired. Please verify again.");
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: "",
      full_name: user.full_name,
      role: user.role,
      team_leader_id: user.team_leader_id || "",
      contact_number: user.contact_number || "",
      // Developer details
      developer_name: user.developer_name || "",
      developer_details: user.developer_details || "",
      developer_address: user.developer_address || "",
      developer_contact: user.developer_contact || "",
      developer_google_location: user.developer_google_location || "",
      // Mandate company details
      mandate_company_name: user.mandate_company_name || "",
      mandate_company_details: user.mandate_company_details || "",
      mandate_company_address: user.mandate_company_address || "",
      mandate_company_contact: user.mandate_company_contact || "",
      sourcing_type: user.sourcing_type || "",
    });
    setEmailError("");
    setPhoneError("");
    setShowEditDialog(true);
  };

  const handleView = (user) => {
    setViewingUser(user);
    setShowViewDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email
    const emailResult = validateEmail(formData.email);
    if (!emailResult.isValid) {
      toast.error(emailResult.error);
      setEmailError(emailResult.error);
      return;
    }

    // Validate phone if provided
    if (formData.contact_number) {
      const phoneResult = validateIndianPhone(formData.contact_number);
      if (!phoneResult.isValid) {
        toast.error(phoneResult.error);
        setPhoneError(phoneResult.error);
        return;
      }
    }

    // Validate CAPTCHA for new user creation
    if (!editingUser && !captchaToken) {
      toast.error("Please complete the CAPTCHA verification");
      setCaptchaError("Please verify you are not a robot");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      if (editingUser) {
        // Update existing user
        const updateData = {
          email: formData.email,
          full_name: formData.full_name,
          role: formData.role,
          team_leader_id: formData.role === "agent" ? formData.team_leader_id : null,
          contact_number: formData.contact_number || null,
        };
        
        // Only include password if it's been changed
        if (formData.password) {
          updateData.password = formData.password;
        }

        await axios.put(`${API}/users/${editingUser.id}`, updateData, { headers });
        toast.success("User updated successfully");
        setShowEditDialog(false);
      } else {
        // Create new user with CAPTCHA
        const phoneResult = validateIndianPhone(formData.contact_number);
        const createData = {
          ...formData,
          team_leader_id: formData.role === "agent" ? formData.team_leader_id : null,
          contact_number: phoneResult.formatted || formData.contact_number || null,
          recaptcha_token: captchaToken,
        };
        await axios.post(`${API}/auth/register`, createData);
        toast.success("User created successfully");
        setShowAddDialog(false);
        // Reset CAPTCHA
        recaptchaRef.current?.reset();
        setCaptchaToken("");
      }

      setFormData({ email: "", password: "", full_name: "", role: "agent", team_leader_id: "", contact_number: "" });
      setEditingUser(null);
      setEmailError("");
      setPhoneError("");
      fetchUsers();
      fetchTeamLeaders();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingUser ? 'update' : 'create'} user`);
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/users/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("User deleted successfully");
      setUsers(users.filter((u) => u.id !== deleteId));
      setDeleteId(null);
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const getTeamLeaderName = (teamLeaderId) => {
    const tl = teamLeaders.find(t => t.id === teamLeaderId);
    return tl ? tl.full_name : "Unassigned";
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin": return "bg-amber-100 text-amber-800";
      case "team_leader": return "bg-purple-100 text-purple-800";
      case "sourcing_manager": return "bg-cyan-100 text-cyan-800";
      case "agent": return "bg-blue-100 text-blue-800";
      case "user": return "bg-green-100 text-green-800";
      default: return "bg-slate-100 text-slate-800";
    }
  };

  const formatRoleName = (role) => {
    switch (role) {
      case "team_leader": return "Team Leader";
      case "sourcing_manager": return "Sourcing Manager";
      case "user": return "Property Seeker";
      default: return role;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0f2744] border-t-transparent" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="fade-in space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#0f2744] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Team Management
            </h1>
            <p className="text-slate-500 mt-1">
              Manage your agents and team leaders
            </p>
          </div>
          <Button
            data-testid="add-user-button"
            onClick={() => setShowAddDialog(true)}
            className="bg-gradient-to-r from-[#c9a227] to-[#ae8220] hover:from-[#ae8220] hover:to-[#96700f] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            style={{ boxShadow: '0 10px 25px -5px rgba(201, 162, 39, 0.4)' }}
          >
            <Plus className="h-5 w-5 mr-2" />
            Add User
          </Button>
        </div>

        {/* Users Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {users.map((user) => (
            <div
              key={user.id}
              data-testid={`user-card-${user.id}`}
              className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-xl transition-all duration-300 relative overflow-hidden group"
              style={{ boxShadow: '0 4px 20px -5px rgba(15, 39, 68, 0.1)' }}
            >
              {/* Decorative gradient bar */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0f2744] via-[#c9a227] to-[#0f2744]" />
              
              <div className="flex items-start justify-between mb-4">
                <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-[#0f2744] to-[#1e3a5f] flex items-center justify-center shadow-lg">
                  <span className="text-2xl font-bold text-white">
                    {user.full_name?.charAt(0) || "U"}
                  </span>
                </div>
                {user.role !== "admin" && (
                  <div className="flex gap-2">
                    <Button
                      data-testid={`view-user-${user.id}`}
                      onClick={() => handleView(user)}
                      size="sm"
                      variant="outline"
                      className="rounded-xl border-[#c9a227] text-[#c9a227] hover:bg-[#c9a227] hover:text-white transition-all"
                      title="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      data-testid={`edit-user-${user.id}`}
                      onClick={() => handleEdit(user)}
                      size="sm"
                      variant="outline"
                      className="rounded-xl border-[#0f2744] text-[#0f2744] hover:bg-[#0f2744] hover:text-white transition-all"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      data-testid={`delete-user-${user.id}`}
                      onClick={() => setDeleteId(user.id)}
                      size="sm"
                      variant="ghost"
                      className="text-[#0f2744] hover:text-white hover:bg-[#0f2744] rounded-xl transition-all"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              <h3 className="text-xl font-bold text-[#0f2744] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {user.full_name}
              </h3>
              <p className="text-sm text-slate-500 flex items-center gap-2 mb-4">
                <Mail className="h-4 w-4" />
                {user.email}
              </p>
              <div className="flex flex-wrap gap-2">
                <span className={`text-xs px-3 py-1.5 rounded-full uppercase font-bold tracking-wide ${getRoleBadgeColor(user.role)}`}>
                  {formatRoleName(user.role)}
                </span>
                {user.role === "agent" && user.team_leader_id && (
                  <span className="text-xs px-3 py-1 rounded bg-slate-100 text-slate-600 flex items-center gap-1">
                    <UsersIcon className="h-3 w-3" />
                    {getTeamLeaderName(user.team_leader_id)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Add User Dialog */}
        <Dialog open={showAddDialog} onOpenChange={(open) => {
          setShowAddDialog(open);
          if (!open) {
            setEmailError("");
            setPhoneError("");
            setCaptchaError("");
            recaptchaRef.current?.reset();
            setCaptchaToken("");
          }
        }}>
          <DialogContent>
            <DialogHeader style={{ background: 'linear-gradient(to right, #0f2744, #1e3a5f)', color: 'white' }}>
              <DialogTitle style={{ color: 'white' }}>Add New User</DialogTitle>
              <DialogDescription style={{ color: '#cbd5e1' }}>Create a new team member account.</DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden flex-1 min-h-0">
              <div className="space-y-4 p-6 bg-white overflow-y-auto flex-1 custom-scrollbar">
              <div>
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  data-testid="user-name-input"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="mt-1 h-10 rounded-sm"
                  required
                  placeholder="John Doe"
                />
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <div className="relative mt-1">
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="user-email-input"
                    value={formData.email}
                    onChange={handleChange}
                    className={`h-10 rounded-sm pr-10 ${emailError ? 'border-red-500' : ''}`}
                    required
                    placeholder="john@realestate.in"
                  />
                  {emailError && <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />}
                  {!emailError && formData.email && <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />}
                </div>
                {emailError && <p className="text-red-500 text-xs mt-1">{emailError}</p>}
              </div>

              <div>
                <Label htmlFor="contact_number">Contact Number *</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="contact_number"
                    name="contact_number"
                    type="tel"
                    data-testid="user-phone-input"
                    value={formData.contact_number}
                    onChange={handleChange}
                    className={`pl-10 h-10 rounded-sm pr-10 ${phoneError ? 'border-red-500' : ''}`}
                    required
                    placeholder="+91 98765 43210"
                  />
                  {phoneError && <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />}
                  {!phoneError && formData.contact_number && <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />}
                </div>
                {phoneError && <p className="text-red-500 text-xs mt-1">{phoneError}</p>}
              </div>

              <div>
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  data-testid="user-password-input"
                  value={formData.password}
                  onChange={handleChange}
                  className="mt-1 h-10 rounded-sm"
                  required
                  placeholder="••••••••"
                />
              </div>

              <div>
                <Label htmlFor="role">Role *</Label>
                <select
                  id="role"
                  name="role"
                  data-testid="user-role-select"
                  value={formData.role}
                  onChange={handleChange}
                  className="mt-1 h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm focus:border-blue-900 focus:ring-1 focus:ring-blue-900"
                  required
                >
                  <option value="agent">Agent</option>
                  <option value="sourcing_manager">Sourcing Manager</option>
                  <option value="team_leader">Team Leader</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {formData.role === "agent" && teamLeaders.length > 0 && (
                <div>
                  <Label htmlFor="team_leader_id">Assign to Team Leader</Label>
                  <select
                    id="team_leader_id"
                    name="team_leader_id"
                    data-testid="team-leader-select"
                    value={formData.team_leader_id}
                    onChange={handleChange}
                    className="mt-1 h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm focus:border-blue-900 focus:ring-1 focus:ring-blue-900"
                  >
                    <option value="">No Team Leader (Direct)</option>
                    {teamLeaders.map((tl) => (
                      <option key={tl.id} value={tl.id}>{tl.full_name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* CAPTCHA Widget */}
              <div className="space-y-2 pt-2 border-t">
                <Label>Verify You're Human *</Label>
                <div className="flex justify-center">
                  <CaptchaWidget
                    ref={recaptchaRef}
                    onChange={handleCaptchaChange}
                    onExpired={handleCaptchaExpired}
                    size="normal"
                  />
                </div>
                {captchaError && (
                  <p className="text-red-500 text-xs text-center flex items-center justify-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {captchaError}
                  </p>
                )}
              </div>

              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddDialog(false)}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="submit-user-button"
                  type="submit"
                  className="bg-blue-900 hover:bg-blue-800 text-white rounded-sm"
                  disabled={!captchaToken}
                >
                  Create User
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={showEditDialog} onOpenChange={() => {
          setShowEditDialog(false);
          setEditingUser(null);
          setFormData({ email: "", password: "", full_name: "", role: "agent", team_leader_id: "", contact_number: "" });
          setEmailError("");
          setPhoneError("");
        }}>
          <DialogContent>
            <DialogHeader style={{ background: 'linear-gradient(to right, #0f2744, #1e3a5f)', color: 'white' }}>
              <DialogTitle style={{ color: 'white' }}>Edit User</DialogTitle>
              <DialogDescription style={{ color: '#cbd5e1' }}>Update user information. Leave password blank to keep current password.</DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden flex-1 min-h-0">
              <div className="space-y-4 p-6 bg-white overflow-y-auto flex-1 custom-scrollbar">
              <div>
                <Label htmlFor="edit_full_name">Full Name *</Label>
                <Input
                  id="edit_full_name"
                  name="full_name"
                  data-testid="edit-user-name-input"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="mt-1 h-10 rounded-sm"
                  required
                  placeholder="John Doe"
                />
              </div>

              <div>
                <Label htmlFor="edit_email">Email *</Label>
                <div className="relative mt-1">
                  <Input
                    id="edit_email"
                    name="email"
                    type="email"
                    data-testid="edit-user-email-input"
                    value={formData.email}
                    onChange={handleChange}
                    className={`h-10 rounded-sm pr-10 ${emailError ? 'border-red-500' : ''}`}
                    required
                    placeholder="john@realestate.in"
                  />
                  {emailError && <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />}
                  {!emailError && formData.email && <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />}
                </div>
                {emailError && <p className="text-red-500 text-xs mt-1">{emailError}</p>}
              </div>

              <div>
                <Label htmlFor="edit_contact_number">Contact Number</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="edit_contact_number"
                    name="contact_number"
                    type="tel"
                    data-testid="edit-user-phone-input"
                    value={formData.contact_number}
                    onChange={handleChange}
                    className={`pl-10 h-10 rounded-sm pr-10 ${phoneError ? 'border-red-500' : ''}`}
                    placeholder="+91 98765 43210"
                  />
                  {phoneError && <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />}
                  {!phoneError && formData.contact_number && <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />}
                </div>
                {phoneError && <p className="text-red-500 text-xs mt-1">{phoneError}</p>}
              </div>

              <div>
                <Label htmlFor="edit_password">New Password (optional)</Label>
                <Input
                  id="edit_password"
                  name="password"
                  type="password"
                  data-testid="edit-user-password-input"
                  value={formData.password}
                  onChange={handleChange}
                  className="mt-1 h-10 rounded-sm"
                  placeholder="Leave blank to keep current password"
                />
              </div>

              <div>
                <Label htmlFor="edit_role">Role *</Label>
                <select
                  id="edit_role"
                  name="role"
                  data-testid="edit-user-role-select"
                  value={formData.role}
                  onChange={handleChange}
                  className="mt-1 h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm focus:border-blue-900 focus:ring-1 focus:ring-blue-900"
                  required
                >
                  <option value="agent">Agent</option>
                  <option value="sourcing_manager">Sourcing Manager</option>
                  <option value="team_leader">Team Leader</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {formData.role === "agent" && teamLeaders.length > 0 && (
                <div>
                  <Label htmlFor="edit_team_leader_id">Assign to Team Leader</Label>
                  <select
                    id="edit_team_leader_id"
                    name="team_leader_id"
                    data-testid="edit-team-leader-select"
                    value={formData.team_leader_id}
                    onChange={handleChange}
                    className="mt-1 h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm focus:border-blue-900 focus:ring-1 focus:ring-blue-900"
                  >
                    <option value="">No Team Leader (Direct)</option>
                    {teamLeaders.map((tl) => (
                      <option key={tl.id} value={tl.id}>{tl.full_name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Sourcing Type (for Sourcing Manager) */}
              {formData.role === "sourcing_manager" && (
                <>
                  <div>
                    <Label htmlFor="edit_sourcing_type">Sourcing Type</Label>
                    <select
                      id="edit_sourcing_type"
                      name="sourcing_type"
                      value={formData.sourcing_type}
                      onChange={handleChange}
                      className="mt-1 h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                    >
                      <option value="">Select Type</option>
                      <option value="from_developer">From Developer</option>
                      <option value="mandate_company">Mandate Company</option>
                    </select>
                  </div>

                  {/* Developer Details */}
                  {formData.sourcing_type === 'from_developer' && (
                    <div className="border-t pt-4 mt-4 space-y-4">
                      <h4 className="font-semibold text-[#0f2744] flex items-center gap-2">
                        <Building className="h-4 w-4" />
                        Developer Details
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="edit_developer_name">Developer Name</Label>
                          <Input
                            id="edit_developer_name"
                            name="developer_name"
                            value={formData.developer_name}
                            onChange={handleChange}
                            className="mt-1 h-10 rounded-sm"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit_developer_contact">Developer Contact</Label>
                          <Input
                            id="edit_developer_contact"
                            name="developer_contact"
                            value={formData.developer_contact}
                            onChange={handleChange}
                            className="mt-1 h-10 rounded-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label htmlFor="edit_developer_details">Developer Details</Label>
                          <Input
                            id="edit_developer_details"
                            name="developer_details"
                            value={formData.developer_details}
                            onChange={handleChange}
                            className="mt-1 h-10 rounded-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label htmlFor="edit_developer_address">Developer Address</Label>
                          <Input
                            id="edit_developer_address"
                            name="developer_address"
                            value={formData.developer_address}
                            onChange={handleChange}
                            className="mt-1 h-10 rounded-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label htmlFor="edit_developer_google_location">Google Location URL</Label>
                          <Input
                            id="edit_developer_google_location"
                            name="developer_google_location"
                            value={formData.developer_google_location}
                            onChange={handleChange}
                            className="mt-1 h-10 rounded-sm"
                            placeholder="https://maps.google.com/..."
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Mandate Company Details */}
                  {formData.sourcing_type === 'mandate_company' && (
                    <div className="border-t pt-4 mt-4 space-y-4">
                      <h4 className="font-semibold text-[#0f2744] flex items-center gap-2">
                        <Building className="h-4 w-4" />
                        Mandate Company Details
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="edit_mandate_company_name">Company Name</Label>
                          <Input
                            id="edit_mandate_company_name"
                            name="mandate_company_name"
                            value={formData.mandate_company_name}
                            onChange={handleChange}
                            className="mt-1 h-10 rounded-sm"
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit_mandate_company_contact">Company Contact</Label>
                          <Input
                            id="edit_mandate_company_contact"
                            name="mandate_company_contact"
                            value={formData.mandate_company_contact}
                            onChange={handleChange}
                            className="mt-1 h-10 rounded-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label htmlFor="edit_mandate_company_details">Company Details</Label>
                          <Input
                            id="edit_mandate_company_details"
                            name="mandate_company_details"
                            value={formData.mandate_company_details}
                            onChange={handleChange}
                            className="mt-1 h-10 rounded-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label htmlFor="edit_mandate_company_address">Company Address</Label>
                          <Input
                            id="edit_mandate_company_address"
                            name="mandate_company_address"
                            value={formData.mandate_company_address}
                            onChange={handleChange}
                            className="mt-1 h-10 rounded-sm"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditDialog(false);
                    setEditingUser(null);
                  }}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="update-user-button"
                  type="submit"
                  className="bg-blue-900 hover:bg-blue-800 text-white rounded-sm"
                >
                  Update User
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete User</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this user? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-[#0f2744] hover:bg-[#1e3a5f]">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* View User Details Dialog */}
        <Dialog open={showViewDialog} onOpenChange={() => {
          setShowViewDialog(false);
          setViewingUser(null);
        }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader style={{ background: 'linear-gradient(to right, #0f2744, #1e3a5f)', color: 'white' }}>
              <DialogTitle style={{ color: 'white' }}>User Details</DialogTitle>
              <DialogDescription style={{ color: '#cbd5e1' }}>Complete registration information</DialogDescription>
            </DialogHeader>

            {viewingUser && (
              <div className="p-6 space-y-6">
                {/* Basic Information */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-[#0f2744] mb-3 flex items-center gap-2">
                    <UserCircle className="h-5 w-5" />
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-500 text-xs">Full Name</Label>
                      <p className="font-medium">{viewingUser.full_name || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Role</Label>
                      <span className={`text-xs px-3 py-1 rounded-full uppercase font-bold ${getRoleBadgeColor(viewingUser.role)}`}>
                        {formatRoleName(viewingUser.role)}
                      </span>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Email</Label>
                      <p className="font-medium flex items-center gap-1">
                        <Mail className="h-4 w-4 text-slate-400" />
                        {viewingUser.email}
                      </p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Contact Number</Label>
                      <p className="font-medium flex items-center gap-1">
                        <Phone className="h-4 w-4 text-slate-400" />
                        {viewingUser.contact_number || '-'}
                      </p>
                    </div>
                    {viewingUser.team_leader_id && (
                      <div>
                        <Label className="text-slate-500 text-xs">Team Leader</Label>
                        <p className="font-medium flex items-center gap-1">
                          <UsersIcon className="h-4 w-4 text-slate-400" />
                          {getTeamLeaderName(viewingUser.team_leader_id)}
                        </p>
                      </div>
                    )}
                    <div>
                      <Label className="text-slate-500 text-xs">Registration Date</Label>
                      <p className="font-medium">
                        {viewingUser.created_at ? new Date(viewingUser.created_at).toLocaleDateString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric'
                        }) : '-'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Developer Details (for Sourcing Managers) */}
                {viewingUser.sourcing_type === 'from_developer' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-[#0f2744] mb-3 flex items-center gap-2">
                      <Building className="h-5 w-5 text-amber-600" />
                      Developer Details
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-500 text-xs">Developer Name</Label>
                        <p className="font-medium">{viewingUser.developer_name || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-slate-500 text-xs">Developer Contact</Label>
                        <p className="font-medium flex items-center gap-1">
                          <Phone className="h-4 w-4 text-slate-400" />
                          {viewingUser.developer_contact || '-'}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-slate-500 text-xs">Developer Details</Label>
                        <p className="font-medium">{viewingUser.developer_details || '-'}</p>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-slate-500 text-xs">Developer Address</Label>
                        <p className="font-medium flex items-center gap-1">
                          <MapPin className="h-4 w-4 text-slate-400" />
                          {viewingUser.developer_address || '-'}
                        </p>
                      </div>
                      {viewingUser.developer_google_location && (
                        <div className="col-span-2">
                          <Label className="text-slate-500 text-xs">Google Location</Label>
                          <a 
                            href={viewingUser.developer_google_location} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="font-medium text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <ExternalLink className="h-4 w-4" />
                            View on Google Maps
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Mandate Company Details (for Sourcing Managers) */}
                {viewingUser.sourcing_type === 'mandate_company' && (
                  <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-[#0f2744] mb-3 flex items-center gap-2">
                      <Building className="h-5 w-5 text-cyan-600" />
                      Mandate Company Details
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-500 text-xs">Company Name</Label>
                        <p className="font-medium">{viewingUser.mandate_company_name || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-slate-500 text-xs">Company Contact</Label>
                        <p className="font-medium flex items-center gap-1">
                          <Phone className="h-4 w-4 text-slate-400" />
                          {viewingUser.mandate_company_contact || '-'}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-slate-500 text-xs">Company Details</Label>
                        <p className="font-medium">{viewingUser.mandate_company_details || '-'}</p>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-slate-500 text-xs">Company Address</Label>
                        <p className="font-medium flex items-center gap-1">
                          <MapPin className="h-4 w-4 text-slate-400" />
                          {viewingUser.mandate_company_address || '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* No Developer/Mandate info message */}
                {viewingUser.role === 'sourcing_manager' && !viewingUser.sourcing_type && (
                  <div className="bg-slate-100 rounded-lg p-4 text-center text-slate-500">
                    <Building className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No developer or mandate company details provided during registration.</p>
                  </div>
                )}

                {/* Edit Button */}
                <div className="flex justify-end pt-4 border-t">
                  <Button
                    onClick={() => {
                      setShowViewDialog(false);
                      handleEdit(viewingUser);
                    }}
                    className="bg-[#0f2744] hover:bg-[#1e3a5f] text-white"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit User
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
